import React from "react";
import type { ApolloClient } from "@apollo/client";
import { useParams } from "react-router-dom";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card, Col, Form, Row } from "react-bootstrap";
import { Formik } from "formik";
import type { FormikProps } from "formik";
import { push } from "store/actions/router";
import {
  chain,
  every,
  filter,
  find,
  get,
  isEmpty,
  isNil,
  isNumber,
  omit,
  pick,
  size,
  some,
  toNumber,
  transform,
} from "lodash";
import queryString from "query-string";
import moment from "moment";
import DatePicker from "react-datepicker";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { employerConstants, payrollConstants } from "actions/types";
import { formatAmount, formatCurrency } from "utils/number";
import { approvePayroll, getPayrollLineItems } from "actions/payrollActions";
import {
  employerContributionAbility,
  userContributionAbilities,
} from "store/selectors/employer";
import {
  getAllEmployerAccounts,
  getAllEmployerGroups,
  getLinkedEmployees,
  getPayrollGroupContributionAbility,
  getUsersForGroup,
} from "actions/employerActions";
import {
  lockedUserProfilesSelector,
  userProfilesSelector,
  usersWithNoContributionChangeSelector,
  usersWithPendingContributionsSelector,
} from "store/selectors/user";
import InfoTooltip from "components/InfoTooltip";
import PayrollSummary from "./PayrollSummary";
import Button from "components/Button";
import IconSpinner from "components/IconSpinner";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import "./PayrollSummary.scss";
import { ScrollToFieldError } from "utils/form";
import type { RootState } from "types/state";

import * as yup from "yup";

interface PendingContribution {
  employeeId?: string;
  amount: number | string;
  id?: string;
}

/** Shape of a raw contribution-change record from the redux selectors. */
interface ContributionChangeLike {
  employeeId?: string;
  amount?: number;
  userCurrentContributionAmount?: number;
}

/** Fields read off a user profile entry (selector types them loosely). */
interface UserProfileFields {
  firstName?: string;
  lastName?: string;
  lastFourOfSsn?: string;
}

interface AmountInputProps {
  amount: number;
  updateContributionAmount: (
    e: React.ChangeEvent<HTMLInputElement>,
    changeProp: PendingContribution
  ) => void;
  changeProp: PendingContribution;
  isInvalid: (changeProp: PendingContribution) => boolean;
  amountRemaining: number;
  canContribute: boolean;
  taxYear?: number;
}

const AmountInput = (props: AmountInputProps) => {
  return (
    <>
      <Form.Control
        name="amount"
        placeholder="Amount"
        type="number"
        value={props.amount}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          props.updateContributionAmount(e, props.changeProp)
        }
        isInvalid={props.isInvalid(props.changeProp)}
      />
      <Form.Control.Feedback type="invalid">
        {props.canContribute
          ? `Amount must be greater than $0 and less than or equal to ${formatCurrency(
              props.amountRemaining
            )} for the ${props.taxYear} tax year.`
          : `User has maxed out their ${props.taxYear} contributions.`}
      </Form.Control.Feedback>
    </>
  );
};

const DATE_FORMAT = "YYYY-MM-DD";

/** Row produced by _getApprovedData / consumed by the approved IconTable. */
interface ApprovedRow {
  employeeName: string;
  lastFourOfSsn?: string;
  amount: string;
  id?: string;
}

/** Row consumed by the locked-user IconTable (a user profile entry). */
interface LockedRow {
  firstName?: string;
  lastName?: string;
  lastFourOfSsn?: string;
}

/** Row produced by _getApprovalData / consumed by the pending IconTable. */
interface ApprovalRow {
  employeeName: string;
  lastFourOfSsn?: string;
  hasLeftPlan: string;
  amount: number;
  changeProp: PendingContribution;
  id?: string;
  amountRemaining: number;
  canContribute: boolean;
  taxYear?: number;
}

interface ContributionAbilityYear {
  taxYear?: number;
  amountRemaining?: number;
}

interface UserContributionAbility {
  userId?: string;
  currentYear?: ContributionAbilityYear;
  priorYear?: ContributionAbilityYear;
  allYears?: ContributionAbilityYear[];
  isWithinPriorYearContributionWindow?: boolean;
}

interface EmployerContributionAbility {
  isWithinPayrollPriorYearContributionWindow?: boolean;
  isWithinPriorYearContributionWindow?: boolean;
  payrollPriorYearCutoffDate?: string;
  payrollTaxYears?: number[];
  currentYear?: ContributionAbilityYear;
  priorYear?: ContributionAbilityYear;
}

interface PayPeriod {
  startDate?: string | null;
  endDate?: string | null;
}

const loadingActions = [
  employerConstants.GET_USERS_FOR_GROUP,
  employerConstants.GET_PAYROLL_GROUP_CONTRIBUTION_ABILITY,
  payrollConstants.GET_PAYROLL_LINE_ITEMS,
  employerConstants.GET_LINKED_EMPLOYEES,
];

const updatingActions = [
  employerConstants.APPROVE_CONTRIBUTION_CHANGES,
  employerConstants.REJECT_CONTRIBUTION_CHANGES,
  employerConstants.DELETE_EMPLOYER_LINKS,
  payrollConstants.APPROVE_PAYROLL,
];

const isFetchingSelector = createLoadingSelector(loadingActions);
const errorSelector = createErrorSelector([
  ...loadingActions,
  ...updatingActions,
]);
const isUpdating = createLoadingSelector(updatingActions);

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const groupId = ownProps.groupId;
  const queryParams = queryString.parse(window.location.search);
  const payPeriodId = get(queryParams, "payPeriodId") as string | undefined;

  return {
    groupId,
    error: errorSelector(state),
    pendingContributionChanges: usersWithPendingContributionsSelector(state),
    usersWhoLeftPlan: state.payroll.usersWhoLeftPlan,
    contributionsWithNoChange: usersWithNoContributionChangeSelector(state),
    isUpdating: isUpdating(state),
    isFetching: isFetchingSelector(state),
    userProfiles: userProfilesSelector(state),
    lockedUserProfiles: lockedUserProfilesSelector(state),
    userContributionAbilities: userContributionAbilities(
      state
    ) as UserContributionAbility[],
    payPeriodId,
    employerContributionAbility: employerContributionAbility(
      state
    ) as EmployerContributionAbility,
    payrolls: state.employer.currentPayrolls,
  };
};

const mapDispatchToProps = {
  getPayrollLineItems,
  getUsersForGroup,
  push,
  getLinkedEmployees,
  approvePayroll,
  getAllEmployerAccounts,
  getAllEmployerGroups,
  getPayrollGroupContributionAbility,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

// react-step-wizard injects these at runtime when the step is rendered inside
// a <StepWizard>; the parent does not pass them explicitly.
interface OwnProps {
  groupId?: string;
  stepName?: string;
  goToNamedStep?: (stepName: string) => void;
  nextStep?: () => void;
  previousStep?: () => void;
  currentStep?: number;
  totalSteps?: number;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface FormValues {
  reason: string | null;
  startDate: string | null;
  endDate: string | null;
}

interface State {
  pendingContributions: PendingContribution[];
  showFinalApprovalPage: boolean;
  newApproved: Record<string, PendingContribution>;
  selectAllPending: boolean;
  checkedPendingRows: Record<string, boolean>;
  startDate: string | null;
  endDate: string | null;
  reason: string | null;
}

export class ApproveOneTimePayrollWorkflow extends React.PureComponent<
  Props,
  State
> {
  constructor(props: Props) {
    // Original JS constructor took no args and called super() with none; the
    // body never reads this.props, so forwarding props to super is inert.
    super(props);

    const queryParams = queryString.parse(window.location.search);
    const startDateQuery = get(queryParams, "startDate") as string | undefined;
    const endDateQuery = get(queryParams, "endDate") as string | undefined;
    const startDate = !isEmpty(startDateQuery)
      ? moment(startDateQuery).format(DATE_FORMAT)
      : null;
    const endDate = !isEmpty(endDateQuery)
      ? moment(endDateQuery).format(DATE_FORMAT)
      : null;

    this.state = {
      pendingContributions: [],
      showFinalApprovalPage: false,
      newApproved: {},
      selectAllPending: false,
      checkedPendingRows: {},
      startDate,
      endDate,
      reason: null,
    };
  }

  componentDidMount() {
    const client = this.props.client as ApolloClient<object>;
    const requests = [
      this.props.getLinkedEmployees(client),
      this.props.getPayrollLineItems(client, this.props.groupId!),
      this.props.getUsersForGroup(client, this.props.groupId!),
      this.props.getAllEmployerAccounts(client),
      this.props.getAllEmployerGroups(client),
      this.props.getPayrollGroupContributionAbility(
        client,
        this.props.groupId!
      ),
    ];
    void Promise.all(requests).then(() => {
      this.setState({
        pendingContributions: this._getAllUserContributions(),
      });
    });
  }

  _getAllUserContributions(): PendingContribution[] {
    const {
      pendingContributionChanges,
      usersWhoLeftPlan,
      contributionsWithNoChange,
    } = this.props;
    return chain({
      ...pendingContributionChanges,
      ...usersWhoLeftPlan,
      ...contributionsWithNoChange,
    } as Record<string, ContributionChangeLike>)
      .map((change) => {
        const amount = this._getContributionAmount(change);
        return { employeeId: change.employeeId, amount, id: change.employeeId };
      })
      .value();
  }

  _selectPendingContribution = (
    userContributionId: string,
    selected: boolean
  ) => {
    this.setState((state) => {
      // if select all pending is true it will only remain true if they are not unchecking
      const selectAllPending = this.state.selectAllPending && selected;
      return {
        checkedPendingRows: {
          ...state.checkedPendingRows,
          [userContributionId]: selected,
        },
        selectAllPending,
      };
    });
  };

  _undoAction = (section: "newApproved", id: string) => {
    this.setState({
      [section]: omit(this.state[section], id),
    });
  };

  _getContributionAmount(user: ContributionChangeLike) {
    const defaultAmount = 0;
    if (isNil(user.amount) && !isNil(user.userCurrentContributionAmount)) {
      return user.userCurrentContributionAmount;
    } else if (
      isNil(user.userCurrentContributionAmount) &&
      !isNil(user.amount)
    ) {
      return user.amount;
    }

    return defaultAmount;
  }

  _getApprovedData = () => {
    return chain(this.state.newApproved)
      .map((approvedContribution: PendingContribution) => {
        const matchingUserProfile = (this.props.userProfiles[
          approvedContribution.employeeId!
        ] || {}) as UserProfileFields;

        const first = matchingUserProfile.firstName;
        const last = matchingUserProfile.lastName;
        const fullName = first + " " + last;

        return {
          employeeName: fullName,
          lastFourOfSsn: matchingUserProfile.lastFourOfSsn,
          amount: formatCurrency(approvedContribution.amount),
          id: approvedContribution.id,
        };
      })
      .value();
  };

  _buildApprovedColumns = (): Array<IconTableColumn<ApprovedRow>> => {
    const columns: Array<IconTableColumn<ApprovedRow>> = [
      {
        label: "Employee Name",
        key: "employeeName",
      },

      {
        label: "Last 4 SSN",
        key: "lastFourOfSsn",
      },
      {
        label: "Contribution Amount",
        key: "amount",
      },
      {
        label: "Action",
        customComponent: (props: ApprovedRow) => {
          return (
            <>
              <span
                className="action-icon icon-link"
                onClick={() => this._undoAction("newApproved", props.id!)}
              >
                Undo
              </span>
            </>
          );
        },
      },
    ];
    return columns;
  };

  _isInvalid = (contribution: PendingContribution, payPeriod: PayPeriod) => {
    const isChecked = this.state.checkedPendingRows[contribution.id!];
    const isNotNumber = !isNumber(contribution.amount);
    // amount may be a string after inline edit; casts preserve the JS
    // relational coercion exactly (the cast is erased at runtime).
    const invalidNumber = (contribution.amount as number) <= 0;
    const contributionAbility = this._getUserContributionAbility(
      contribution.employeeId
    );
    const { amountRemaining, canContribute } =
      this._getContributionAbilityDetails(contributionAbility, payPeriod);
    const isWithinContributionRange =
      (contribution.amount as number) > 0 &&
      (contribution.amount as number) <= amountRemaining;

    return (
      isChecked &&
      (isNotNumber ||
        invalidNumber ||
        !canContribute ||
        !isWithinContributionRange)
    );
  };

  _getUserContributionAbility = (userId?: string) => {
    const contributionAbility = find(
      this.props.userContributionAbilities,
      (ability) => ability.userId === userId
    );
    return contributionAbility;
  };

  _getContributionAbilityDetails = (
    contributionAbility: UserContributionAbility | undefined,
    payPeriod: PayPeriod
  ) => {
    const taxYear = this._getTaxYear(payPeriod);
    // lodash find iterates the ability's values (currentYear/priorYear/etc.);
    // cast to the year-record view to match the original loose iteration.
    const targetContributionAbility = find(
      contributionAbility as
        | Record<string, ContributionAbilityYear>
        | undefined,
      (year) => year.taxYear === taxYear
    );
    const amountRemaining = get(
      targetContributionAbility,
      "amountRemaining",
      0
    );
    const canContribute = amountRemaining > 0;
    return {
      amountRemaining,
      canContribute,
    };
  };

  _getTaxYear = (payPeriod: PayPeriod): number | undefined => {
    const {
      isWithinPayrollPriorYearContributionWindow,
      payrollPriorYearCutoffDate,
      currentYear,
      priorYear,
    } = this.props.employerContributionAbility;

    const { payPeriodId } = this.props;

    if (payPeriodId) {
      const matchedPayroll = find(
        this.props.payrolls,
        (payroll) => payroll.payPeriodId === payPeriodId
      );
      if (matchedPayroll) {
        return matchedPayroll.taxYear as number | undefined;
      }
    }

    const payPeriodDates = [
      get(payPeriod, "startDate"),
      get(payPeriod, "endDate"),
    ].filter((date) => !isNil(date));
    const taxYearReferenceDate = moment.min(
      payPeriodDates.map((date) => moment(date))
    );
    const payPeriodIsBeforeCutoffDate = taxYearReferenceDate.isSameOrBefore(
      moment(payrollPriorYearCutoffDate)
    );
    const isValidPriorYearPayroll =
      isWithinPayrollPriorYearContributionWindow &&
      payPeriodIsBeforeCutoffDate &&
      taxYearReferenceDate.year() === priorYear!.taxYear;

    // PRESERVE: reads currentYear.taxYear unconditionally — throws if
    // currentYear is absent (locked by characterization test); non-null
    // assertion keeps that throw-on-undefined behavior, no ?. guard added.
    return isValidPriorYearPayroll ? priorYear!.taxYear : currentYear!.taxYear;
  };

  _approveSelected = () => {
    const newApproved = transform(
      this.state.checkedPendingRows,
      (acc: Record<string, PendingContribution | undefined>, row, id) => {
        if (row) {
          acc[id] = find(
            this.state.pendingContributions,
            (contribution) => contribution.id === id
          );
          return acc;
        }
        return acc;
      },
      {} as Record<string, PendingContribution | undefined>
    );

    this.setState({
      newApproved: {
        ...this.state.newApproved,
        ...newApproved,
      } as Record<string, PendingContribution>,
      selectAllPending: false,
      checkedPendingRows: {},
    });
  };

  _updateContributionAmount = (
    event: React.ChangeEvent<HTMLInputElement>,
    contribution: PendingContribution
  ) => {
    const amount = event.target.value;
    const newAmount = !isEmpty(amount) ? formatAmount(amount) : amount;
    const { pendingContributions } = this.state;
    const updatedPendingContributions = pendingContributions.map((c) => {
      if (c.id === contribution.id) {
        return { ...c, amount: newAmount };
      }
      return c;
    });

    this.setState({
      pendingContributions: updatedPendingContributions,
    });
  };

  _selectAllPending = (checked: boolean) => {
    const pendingChanges = filter(
      this.state.pendingContributions,
      (change) => !this.state.newApproved[change.employeeId!]
    );
    this.setState({
      selectAllPending: checked,
      checkedPendingRows: transform(
        pendingChanges,
        (acc: Record<string, boolean>, val) => {
          acc[val.id!] = checked;
          return acc;
        },
        {} as Record<string, boolean>
      ),
    });
  };

  _getApprovalData = (payPeriod: PayPeriod): ApprovalRow[] => {
    const pendingContributions = this.state.pendingContributions;
    return pendingContributions
      .filter((change) => {
        const newApprovedId = !this.state.newApproved[change.employeeId!];
        return newApprovedId;
      })
      .map((change) => {
        const matchingUserProfile = (this.props.userProfiles[
          change.employeeId!
        ] || {}) as UserProfileFields;
        const hasLeft = some(
          this.props.usersWhoLeftPlan,
          (user: ContributionChangeLike) =>
            user.employeeId === change.employeeId
        );

        const first = matchingUserProfile.firstName;
        const last = matchingUserProfile.lastName;
        const fullName = first + " " + last;
        const contributionAbility = this._getUserContributionAbility(
          change.employeeId
        );
        const { amountRemaining, canContribute } =
          this._getContributionAbilityDetails(contributionAbility, payPeriod);

        return {
          employeeName: fullName,
          lastFourOfSsn: matchingUserProfile.lastFourOfSsn,
          hasLeftPlan: hasLeft ? "User Left Plan" : "Active",
          amount: toNumber(change.amount),
          changeProp: change,
          id: change.id,
          amountRemaining: toNumber(amountRemaining),
          canContribute,
          taxYear: this._getTaxYear(payPeriod),
        };
      });
  };

  _buildPendingColumns = (payPeriod: PayPeriod) => {
    const columns: Array<IconTableColumn<ApprovalRow>> = [
      {
        label: "Employee Name",
        key: "employeeName",
      },

      {
        label: "Last 4 SSN",
        key: "lastFourOfSsn",
      },
      {
        label: "Status",
        key: "hasLeftPlan",
      },
      {
        label: "Current Amount",
        metadata: {
          updateContributionAmount: this._updateContributionAmount,
          isInvalid: (contribution: PendingContribution) =>
            this._isInvalid(contribution, payPeriod),
        },
        // updateContributionAmount/isInvalid are injected at runtime via the
        // metadata above (IconTable spreads metadata into the component's
        // props); cast bridges that runtime-only contract.
        customComponent: AmountInput as unknown as React.ComponentType<
          ApprovalRow & Record<string, unknown>
        >,
      },
    ];
    return columns;
  };

  _checkedRowsHaveValidAmount = (payPeriod: PayPeriod) => {
    const allCheckedContributionsAreValid = every(
      this.state.checkedPendingRows,
      (isChecked, contributionId) => {
        if (isChecked) {
          const contribution = find(this.state.pendingContributions, {
            id: contributionId,
          });
          return !this._isInvalid(contribution!, payPeriod);
        } else {
          return true;
        }
      }
    );
    return allCheckedContributionsAreValid;
  };

  _getPendingChangesTable(props: FormikProps<FormValues>) {
    const payPeriod = pick(props.values, ["startDate", "endDate"]);
    const columns = this._buildPendingColumns(payPeriod);
    const data = this._getApprovalData(payPeriod);

    const noCheckedRow = every(
      this.state.checkedPendingRows,
      (rowChecked) => !rowChecked
    );
    const buttonsDisabled =
      noCheckedRow || !this._checkedRowsHaveValidAmount(payPeriod);
    const table = !isEmpty(data) ? (
      <>
        <IconTable
          columns={columns}
          data={data}
          hasSelectableRows
          uniqueRowIdentifier="id"
          onSelectAll={(e) => this._selectAllPending(e.target.checked)}
          hasSelectedAll={this.state.selectAllPending}
          onRowSelect={(id, checked) => {
            this._selectPendingContribution(id, checked);
          }}
          selectedRows={this.state.checkedPendingRows}
        />
        <div className="button-row">
          <Button
            size="sm"
            name="action"
            disabled={buttonsDisabled}
            btnLabel="Approve Selected"
            onClick={this._approveSelected}
          />
        </div>
      </>
    ) : null;
    return this._buildCardedTable(table, `All Users in Group (${size(data)})`);
  }

  _getApprovedTable() {
    const columns = this._buildApprovedColumns();
    const data = this._getApprovedData();

    const table = !isEmpty(data) ? (
      <>
        <IconTable columns={columns} data={data} />
      </>
    ) : null;
    return this._buildCardedTable(
      table,
      `Approved Contributions (${size(data)})`,
      "Only contributions larger than $0 in this table will be processed."
    );
  }

  _buildLockedUsersColumns = (): Array<IconTableColumn<LockedRow>> => {
    const columns: Array<IconTableColumn<LockedRow>> = [
      {
        label: "Employee Name",
        customComponent: (props: LockedRow) => {
          return (
            <>
              {props.firstName} {props.lastName}
            </>
          );
        },
      },

      {
        label: "Last 4 SSN",
        key: "lastFourOfSsn",
      },
      {
        label: "Contribution Amount	",
        customComponent: () => {
          return <>{formatCurrency(0)}</>;
        },
      },
    ];
    return columns;
  };

  _getLockedUserTable() {
    const columns = this._buildLockedUsersColumns();

    const { lockedUserProfiles } = this.props;
    // lockedUserProfiles is keyed by id; IconTable maps over its values at
    // runtime (lodash map handles objects), so cast to the row-array view.
    const data = lockedUserProfiles as unknown as LockedRow[];

    const table = !isEmpty(data) ? (
      <>
        <IconTable columns={columns} data={data} />
      </>
    ) : null;
    return this._buildCardedTable(
      table,
      `Locked User Accounts (${size(data)})`,
      "These users have been locked by an Icon administrator and are unable to make contributions at this time."
    );
  }

  _buildCardedTable(
    table: React.ReactNode,
    title: string,
    description?: string,
    refName?: string
  ) {
    const displayDescription = !isEmpty(description);

    return (
      <Card
        className="approval-table"
        ref={(node) => {
          if (refName) {
            // dynamic ref assignment preserved from JS; refName is never
            // passed by any caller, so this branch is inert.
            (this as unknown as Record<string, unknown>)[refName] = node;
          }
        }}
      >
        <Card.Header>
          {title}{" "}
          {displayDescription && (
            <InfoTooltip iconSize={16} tooltipBody={description} />
          )}
        </Card.Header>

        {table}
      </Card>
    );
  }

  _getOneTimePayrollInfo({
    handleChange,
    handleBlur,
    values,
    touched,
    errors,
  }: FormikProps<FormValues>) {
    const isStartDateInvalid =
      touched.startDate && !!errors.startDate && isNil(values.startDate);
    const isEndDateInvalid =
      touched.endDate && !!errors.endDate && isNil(values.endDate);

    const minDateForStartDateSelection = this._getMinDate(
      values.startDate,
      true
    );
    const maxDateForStartDateSelection = this._getMaxDate(values.endDate);

    const minDateForEndDateSelection = this._getMinDate(values.startDate);
    const maxDateForEndDateSelection = this._getMaxDate(values.endDate, true);
    return (
      <Card>
        <Card.Body>
          <Card.Title>Payroll Information</Card.Title>
          {this.props.employerContributionAbility
            .isWithinPayrollPriorYearContributionWindow && (
            <div>
              Prior year contributions can only be created before{" "}
              {
                this.props.employerContributionAbility
                  .payrollPriorYearCutoffDate
              }
              .
              <br />
              <br />
            </div>
          )}
          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label>Pay Period Start Date</Form.Label>
              <div>
                <DatePicker
                  className={`form-control ${
                    isStartDateInvalid ? "is-invalid" : ""
                  }`}
                  wrapperClassName="w-100"
                  name="startDate"
                  value={
                    (values.startDate &&
                      moment(values.startDate).toDate()) as unknown as string
                  }
                  dateFormat="MM/dd/yyyy"
                  selected={
                    (values.startDate &&
                      moment(values.startDate).toDate()) as Date | null
                  }
                  onChange={(value: Date | null) => {
                    handleChange({
                      target: { name: "startDate", value },
                    } as unknown as React.ChangeEvent<HTMLInputElement>);
                  }}
                  onBlur={handleBlur}
                  minDate={minDateForStartDateSelection}
                  maxDate={maxDateForStartDateSelection}
                />
              </div>
              {isStartDateInvalid && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  Start date is a required field.
                </div>
              )}
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>Pay Period End Date</Form.Label>
              <div>
                <DatePicker
                  className={`form-control ${
                    isEndDateInvalid ? "is-invalid" : ""
                  }`}
                  wrapperClassName="w-100"
                  name="endDate"
                  value={
                    (values.endDate &&
                      moment(values.endDate).toDate()) as unknown as string
                  }
                  dateFormat="MM/dd/yyyy"
                  selected={
                    (values.endDate &&
                      moment(values.endDate).toDate()) as Date | null
                  }
                  onChange={(value: Date | null) => {
                    handleChange({
                      target: { name: "endDate", value },
                    } as unknown as React.ChangeEvent<HTMLInputElement>);
                  }}
                  onBlur={handleBlur}
                  minDate={minDateForEndDateSelection}
                  maxDate={maxDateForEndDateSelection}
                />
              </div>
              {isEndDateInvalid && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  End date is a required field.
                </div>
              )}
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label>Reason for payroll</Form.Label>
              <Form.Control
                name="reason"
                placeholder="Reason"
                value={values.reason as string}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.reason && !!errors.reason}
                // PRESERVE: references a non-existent "amount" field (reason
                // form has no amount) — a pre-existing quirk; touched.amount /
                // errors.amount are always undefined, so isValid is undefined.
                // @ts-expect-error amount is not a field of FormValues
                isValid={touched.amount && !errors.amount}
              />
              <Form.Control.Feedback type="invalid">
                Reason is a required field.
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
        </Card.Body>
      </Card>
    );
  }

  _getMinDate = (startDate?: string | null, ignoreStartDate = false) => {
    if (!isNil(startDate) && !ignoreStartDate) {
      return moment(startDate).toDate();
    }

    return moment().subtract(6, "months").toDate();
  };

  _getMaxDate = (endDate?: string | null, ignoreEndDate = false) => {
    if (!isNil(endDate) && !ignoreEndDate) {
      return moment(endDate).toDate();
    }

    return moment().add(1, "months").toDate();
  };

  _getApprovalLayout = () => {
    const { newApproved } = this.state;
    const hasTakenAllActions =
      size(newApproved) > 0 &&
      isNil(find(newApproved, (c: PendingContribution) => c.amount === 0));

    const schema = yup.object({
      reason: yup.string().label("Reason").required(),
      endDate: yup.string().label("End Date").required(),
      startDate: yup.string().label("Start Date").required(),
    });

    return (
      <Formik<FormValues>
        validateOnChange={false}
        validationSchema={schema}
        initialValues={{
          ...pick(this.state, ["reason", "startDate", "endDate"]),
        }}
        onSubmit={(values) =>
          this.setState({ ...values, showFinalApprovalPage: true })
        }
      >
        {(props) => (
          <Form onSubmit={props.handleSubmit}>
            <ScrollToFieldError />

            <Row>
              <Col>{this._getOneTimePayrollInfo(props)}</Col>
            </Row>
            <Row>
              <Col>{this._getPendingChangesTable(props)}</Col>
            </Row>
            {!isEmpty(this.props.lockedUserProfiles) && (
              <Row>
                <Col>{this._getLockedUserTable()}</Col>
              </Row>
            )}
            <Row>
              <Col md={12}>{this._getApprovedTable()}</Col>
              <Col md={12} style={{ textAlign: "right" }}>
                <div className="button-row">
                  <Button
                    btnLabel="Cancel"
                    color="cancel"
                    name="cancel"
                    onClick={() => this.props.push("/dashboard/contributions")}
                  />
                  <Button
                    name="action"
                    btnLabel="Continue"
                    disabled={!hasTakenAllActions}
                  />
                </div>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    );
  };

  _getSummaryLayout = () => {
    const dateFormat = "YYYY-MM-DD";
    const startDate = moment(this.state.startDate).format(dateFormat);
    const endDate = moment(this.state.endDate).format(dateFormat);
    const processingDate = moment().format(dateFormat);
    const payPeriod = { startDate, endDate };
    return (
      <PayrollSummary
        startDate={startDate}
        endDate={endDate}
        allApproved={
          Object.values(this.state.newApproved) as React.ComponentProps<
            typeof PayrollSummary
          >["allApproved"]
        }
        processingDate={processingDate}
        groupId={this.props.groupId!}
        onCancel={() => this.setState({ showFinalApprovalPage: false })}
        isOneTimePayrollContribution={true}
        reason={this.state.reason as string | undefined}
        payPeriodId={this.props.payPeriodId}
        taxYear={this._getTaxYear(payPeriod)}
      />
    );
  };

  render() {
    let content;

    if (this.props.isFetching) {
      content = <IconSpinner centered />;
    } else if (this.state.showFinalApprovalPage) {
      content = this._getSummaryLayout();
    } else {
      content = this._getApprovalLayout();
    }
    return <div id="approval-workflow">{content}</div>;
  }
}

const ConnectedApproveOneTimePayrollWorkflow = connector(
  withApollo<Props>(ApproveOneTimePayrollWorkflow)
);

const ApproveOneTimePayrollWorkflow_WithParams = (props: OwnProps) => {
  const { id: groupId } = useParams();
  return (
    <ConnectedApproveOneTimePayrollWorkflow {...props} groupId={groupId} />
  );
};

export default ApproveOneTimePayrollWorkflow_WithParams;
